// Builds React Flow nodes + edges for 4 mindmap types from profile data

const CENTER_X = 700
const CENTER_Y = 500
const BRANCH_R = 320
const LEAF_R = 240

function uid(prefix, ...parts) {
  return `${prefix}-${parts.join('-')}`
}

function radialPos(cx, cy, angle, r) {
  return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r }
}

function makeCenterNode(id, label, sublabel) {
  return {
    id,
    type: 'mindmapCenter',
    position: { x: CENTER_X, y: CENTER_Y },
    data: { label, sublabel },
  }
}

function makeBranchNode(id, label, position) {
  return {
    id,
    type: 'mindmapBranch',
    position,
    data: { label },
  }
}

function makeLeafNode(id, label, position) {
  return {
    id,
    type: 'mindmapLeaf',
    position,
    data: { label },
  }
}

function makeEdge(source, target) {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
    type: 'bezier',
    style: { stroke: 'rgba(180,185,230,0.35)', strokeWidth: 1.5 },
    animated: false,
  }
}

// Distribute branches evenly around center, leaves around their branch
function buildBranches(centerId, branches) {
  const nodes = []
  const edges = []
  const count = branches.length

  branches.forEach((branch, bi) => {
    const angle = (2 * Math.PI * bi) / count - Math.PI / 2
    const bPos = radialPos(CENTER_X, CENTER_Y, angle, BRANCH_R)
    const bId = uid('b', centerId, bi)

    nodes.push(makeBranchNode(bId, branch.label, bPos))
    edges.push(makeEdge(centerId, bId))

    const leaves = (branch.leaves || []).filter(Boolean).slice(0, 8)
    const lcount = leaves.length
    if (!lcount) return

    const spread = Math.min(Math.PI * 0.6, (lcount * 0.35))
    leaves.forEach((leaf, li) => {
      const lAngle = lcount === 1
        ? angle
        : angle - spread / 2 + (spread / (lcount - 1)) * li
      const lPos = radialPos(bPos.x, bPos.y, lAngle, LEAF_R)
      const lId = uid('l', bId, li)
      nodes.push(makeLeafNode(lId, String(leaf), lPos))
      edges.push(makeEdge(bId, lId))
    })
  })

  return { nodes, edges }
}

// ─── 4 map builders ────────────────────────────────────────────────────────

export function buildPersonalityMap(profile) {
  const p = profile?.personality_data || profile?.personality || {}
  const centerId = 'personality-center'
  const center = makeCenterNode(
    centerId,
    p.mbti || 'Личность',
    p.archetype || ''
  )

  const bf = p.big_five || {}
  const big5Labels = [
    bf.openness != null && `Открытость ${bf.openness}`,
    bf.conscientiousness != null && `Добросовестность ${bf.conscientiousness}`,
    bf.extraversion != null && `Экстраверсия ${bf.extraversion}`,
    bf.agreeableness != null && `Доброжелательность ${bf.agreeableness}`,
    bf.neuroticism != null && `Нейротизм ${bf.neuroticism}`,
  ].filter(Boolean)

  const branches = [
    { label: 'Big Five', leaves: big5Labels },
    { label: 'Ценности', leaves: p.core_values || [] },
    { label: 'Сильные стороны', leaves: p.strengths || [] },
    { label: 'Зоны роста', leaves: p.growth_areas || [] },
    { label: 'Стиль', leaves: [p.communication_style, p.decision_making].filter(Boolean) },
  ]

  const { nodes, edges } = buildBranches(centerId, branches)
  return { nodes: [center, ...nodes], edges }
}

export function buildCompetenciesMap(profile) {
  const c = profile?.competencies_data || profile?.competencies || {}
  const centerId = 'comp-center'
  const center = makeCenterNode(centerId, 'Компетенции', c.learning_style || '')

  const topComp = (c.top_competencies || []).map((tc) =>
    typeof tc === 'string' ? tc : `${tc.name}${tc.level ? ` · ${tc.level}` : ''}`
  )

  const branches = [
    { label: 'Технические навыки', leaves: c.technical_skills || [] },
    { label: 'Soft Skills', leaves: c.soft_skills || [] },
    { label: 'Топ компетенции', leaves: topComp },
    { label: 'Зоны развития', leaves: c.development_areas || [] },
    { label: 'Лидерство', leaves: [c.leadership_potential].filter(Boolean) },
  ]

  const { nodes, edges } = buildBranches(centerId, branches)
  return { nodes: [center, ...nodes], edges }
}

export function buildMotivationMap(profile) {
  const m = profile?.motivation_data || profile?.motivation || {}
  const centerId = 'motiv-center'
  const center = makeCenterNode(
    centerId,
    'Мотивация',
    m.purpose_statement ? m.purpose_statement.slice(0, 60) + (m.purpose_statement.length > 60 ? '…' : '') : ''
  )

  const branches = [
    { label: 'Движущие силы', leaves: m.primary_drivers || [] },
    { label: 'Ценности в действии', leaves: m.values_in_action || [] },
    { label: 'Источники энергии', leaves: m.energy_sources || [] },
    { label: 'Демотиваторы', leaves: m.demotivators || [] },
    { label: 'Внутренние', leaves: m.intrinsic_motivators || [] },
    { label: 'Внешние', leaves: m.extrinsic_motivators || [] },
  ]

  const { nodes, edges } = buildBranches(centerId, branches)
  return { nodes: [center, ...nodes], edges }
}

export function buildCareerMap(profile) {
  const ca = profile?.career_data || profile?.career || {}
  const centerId = 'career-center'
  const center = makeCenterNode(
    centerId,
    'Карьера',
    ca.work_environment ? ca.work_environment.slice(0, 50) : ''
  )

  const branches = [
    { label: 'Идеальные роли', leaves: ca.ideal_roles || [] },
    { label: 'Индустрии', leaves: ca.industries || [] },
    { label: 'Краткосрочные цели', leaves: ca.short_term_goals || [] },
    { label: 'План действий', leaves: ca.action_plan || [] },
    { label: 'Избегать', leaves: ca.avoid || [] },
    { label: 'Долгосрочное видение', leaves: ca.long_term_vision ? [ca.long_term_vision.slice(0, 80)] : [] },
  ]

  const { nodes, edges } = buildBranches(centerId, branches)
  return { nodes: [center, ...nodes], edges }
}
